import type { Hit } from 'instantsearch.js';
import { useHits } from 'react-instantsearch-core';

import { asPathOrEmpty } from '@/utils/urlUtils';

import type { SearchViewType } from '@/components/Search/types';
import { useDebounceFn } from '@/hooks/useDebounceFn';
import { useRouter } from '@/hooks/useRouter';
import type {
  StoreHit,
  StrapHit,
  WatchHit,
} from '@/integrations/algolia/types';
import { searchStateActions } from '@/states/searchState';

import * as M from './messages';
import * as S from './MoreResultsButton.styles';

interface MoreResultsButtonProps {
  finalHits?: Hit<WatchHit | StrapHit | StoreHit>[];
  hitType?: SearchViewType;
  initialResults?: any;
  isOverlay?: boolean;
  nbHits?: number;
  onClick?: () => void;
}

export const MoreResultsButton = ({
  finalHits,
  hitType,
  initialResults,
  isOverlay = false,
  nbHits,
  onClick,
}: MoreResultsButtonProps) => {
  const { results } = useHits();

  let isLastPage = results?.hits.length === nbHits;
  if (!results?.hits || results?.nbHits === 0) {
    isLastPage = initialResults?.hits?.length === initialResults?.nbHits;
  }

  if (finalHits && finalHits.length === nbHits) {
    isLastPage = true;
  }

  const { asPath, paths, push, query, replace } = useRouter();
  const searchType =
    (asPathOrEmpty(asPath).split('/')[3] as SearchViewType) ?? 'watches';
  const page = Number(query.page) || 1;

  const { run: overlayLoadMore } = useDebounceFn(() => {
    searchStateActions.addOverlayPage();
  }, 500);

  const getIndex = (): SearchViewType => {
    switch (hitType) {
      case 'watches':
        return 'watches';

      case 'straps':
        return 'straps';

      case 'stores':
        return 'stores';

      default:
        return 'all';
    }
  };

  const redirectToSpecificProductList = () => {
    const index = getIndex();
    delete query.urlParts;
    push(
      paths.search[index].url({
        ...query,
        query: {
          page: page + 1,
        },
      }),
      undefined,
      { shallow: true },
    );
  };

  const handleClick = () => {
    onClick?.();

    if (searchType === 'all') {
      redirectToSpecificProductList();
      return;
    }
    if (isOverlay) {
      overlayLoadMore();
      return;
    }

    delete query.urlParts;
    replace(
      paths.search[searchType].url({
        ...query,
        query: {
          page: page + 1,
        },
      }),
      undefined,
      { shallow: true },
    );
  };

  if (isLastPage) {
    return null;
  }

  return (
    <S.MoreResultsButton data-test-id='MoreResultsButton' onClick={handleClick}>
      {M.MoreResults}
    </S.MoreResultsButton>
  );
};
